import { useCallback, useEffect, useLayoutEffect } from "react";

import { env } from "@/config/env";

import {
  type BookingIframeVars,
  bookingIframeVarsMessagePayloadSchema,
  bookingIframeVarsSchema,
} from "../schemas/bookingIframeVarsSchema";

import {
  GLEPNIR_BOOKING_SESSION_DATE_KEY,
  STORAGE_KEY as BOOKING_SESSION_STATE_KEY,
} from "./useBookingSessionStorage";

export const IFRAME_VARS_KEY = "booking_iframe_vars";
export const IFRAME_VARS_READY_EVENT = "BOOKING_IFRAME_VARS_READY";
export const IFRAME_VARS_MESSAGE_TYPE = "BOOKING_IFRAME_VARS";
export const IFRAME_CLEAR_SESSION_STORAGE_MESSAGE_TYPE =
  "BOOKING_IFRAME_CLEAR_SESSION_STORAGE";

type UseBookingIframeVarsParams = {
  enabled?: boolean;
  setCoachData: (data: { id?: number; name?: string }) => void;
  setCustomerId: (id: number) => void;
  setMemberPrefill: (prefill: {
    memberName?: string;
    memberEmail?: string;
    memberPhone?: string;
  }) => void;
  cleanBookingState: () => void;
  setRescheduleSessionId: (id: number) => void;
  setTrialInfo: (info: { isTrial?: boolean; trialEndDate?: string }) => void;
  setBookedByUserId: (id: number | undefined) => void;
  setPreSessionNotesEnabled: (enabled: boolean) => void;
  setMultiSessionBookingEnabled: (enabled: boolean) => void;
};

type BookingIframeVarsMessage = {
  type: typeof IFRAME_VARS_MESSAGE_TYPE;
  payload: unknown;
};

const parseIframeVars = (val: unknown): BookingIframeVars | null => {
  const result = bookingIframeVarsSchema.safeParse(val);
  return result.success ? result.data : null;
};

const readFromStorage = (): BookingIframeVars | null => {
  try {
    const raw = sessionStorage.getItem(IFRAME_VARS_KEY);
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    return parseIframeVars(parsed);
  } catch {
    return null;
  }
};

const writeToStorage = (vars: BookingIframeVars): void => {
  try {
    sessionStorage.setItem(IFRAME_VARS_KEY, JSON.stringify(vars));
  } catch {
    // sessionStorage may be unavailable in certain iframe contexts — fail silently
  }
};

const getAllowedOrigins = (): string[] => {
  return (
    env.VITE_BOOKING_IFRAME_ALLOWED_ORIGINS?.split(",")
      .map((origin) => origin.trim())
      .filter(Boolean) ?? []
  );
};

const isLocalhostMatch = (origin: string, allowedOrigin: string): boolean => {
  if (allowedOrigin !== "http://localhost") {
    return false;
  }

  try {
    const parsedOrigin = new URL(origin);
    return (
      parsedOrigin.protocol === "http:" && parsedOrigin.hostname === "localhost"
    );
  } catch {
    return false;
  }
};

const isWildcardSubdomainMatch = (
  origin: string,
  allowedOrigin: string,
): boolean => {
  const wildcardPrefix = "https://*.";

  if (!allowedOrigin.startsWith(wildcardPrefix)) {
    return false;
  }

  try {
    const parsedOrigin = new URL(origin);
    const allowedDomain = allowedOrigin.slice(wildcardPrefix.length);

    return (
      parsedOrigin.protocol === "https:" &&
      parsedOrigin.hostname !== allowedDomain &&
      parsedOrigin.hostname.endsWith(`.${allowedDomain}`)
    );
  } catch {
    return false;
  }
};

const isAllowedMessageOrigin = (origin: string): boolean => {
  return getAllowedOrigins().some((allowedOrigin) => {
    return (
      allowedOrigin === origin ||
      isLocalhostMatch(origin, allowedOrigin) ||
      isWildcardSubdomainMatch(origin, allowedOrigin)
    );
  });
};

const isBookingIframeVarsMessage = (
  data: unknown,
): data is BookingIframeVarsMessage => {
  return (
    Boolean(data) &&
    typeof data === "object" &&
    (data as Record<string, unknown>).type === IFRAME_VARS_MESSAGE_TYPE
  );
};

const tryRemoveSessionStorageKeysFromMessage = (
  data: unknown,
  onSuccessfulClear?: () => void,
): boolean => {
  if (!data || typeof data !== "object") {
    return false;
  }
  const record = data as Record<string, unknown>;
  if (record.type !== IFRAME_CLEAR_SESSION_STORAGE_MESSAGE_TYPE) {
    return false;
  }

  const payload = record.payload;
  if (
    !payload ||
    typeof payload !== "object" ||
    !Array.isArray((payload as Record<string, unknown>).keys)
  ) {
    return true;
  }

  try {
    for (const key of (payload as { keys: unknown[] }).keys) {
      if (typeof key !== "string") {
        continue;
      }
      sessionStorage.removeItem(key);
      // Glepnir uses this key name; Lotus persists date/slot under booking_session_state.
      if (key === GLEPNIR_BOOKING_SESSION_DATE_KEY) {
        sessionStorage.removeItem(BOOKING_SESSION_STATE_KEY);
      }
    }
  } catch {
    // sessionStorage may be unavailable in certain iframe contexts — fail silently
  }

  onSuccessfulClear?.();
  return true;
};

const buildVarsFromMessagePayload = (
  payload: unknown,
): BookingIframeVars | null => {
  // The message schema layers `memberPhone: string | number → string` coercion
  // and `coachName` non-empty on top of the canonical schema, so we surface a
  // canonical object regardless of which the host sent.
  const result = bookingIframeVarsMessagePayloadSchema.safeParse(payload);
  return result.success ? result.data : null;
};

const buildVarsFromWindow = (
  payload: Window["__BOOKING_IFRAME_VARS__"],
): BookingIframeVars | null => {
  // Window vars are always written by us in canonical form (postMessage
  // handler or Caladan's lotusBookingScript). Validating against the same
  // schema as sessionStorage keeps the read paths consistent.
  return parseIframeVars(payload);
};

// Rehydrates coach/customer identity and member prefill data across page
// refreshes. On first load the parent dispatches BOOKING_IFRAME_VARS_READY
// after setting window.__BOOKING_IFRAME_VARS__. This hook persists those
// values to sessionStorage so that on a refresh — when the event may fire
// before React mounts — they can be rehydrated immediately without waiting
// for the parent.
//
// Booking UI reset (`cleanBookingState`) runs only after a validated
// BOOKING_IFRAME_CLEAR_SESSION_STORAGE message from an allowed origin (e.g.
// Glepnir before BOOKING_IFRAME_VARS prefill), not on each BOOKING_IFRAME_VARS.
export const useBookingIframeVars = ({
  enabled = false,
  setCoachData,
  setCustomerId,
  setMemberPrefill,
  cleanBookingState,
  setRescheduleSessionId,
  setTrialInfo,
  setBookedByUserId,
  setPreSessionNotesEnabled,
  setMultiSessionBookingEnabled,
}: UseBookingIframeVarsParams): void => {
  const applyVars = useCallback(
    (vars: BookingIframeVars) => {
      setCoachData({ id: vars.coachId, name: vars.coachName });
      setCustomerId(vars.customerId);
      setMemberPrefill({
        memberName: vars.memberName,
        memberEmail: vars.memberEmail,
        memberPhone: vars.memberPhone,
      });
      if (vars.rescheduleSessionId !== undefined) {
        setRescheduleSessionId(vars.rescheduleSessionId);
      }
      setTrialInfo({
        isTrial: vars.isTrial,
        trialEndDate: vars.trialEndDate,
      });
      setBookedByUserId(vars.bookedByUserId);
      // Absent flag → false, keeping plain-web booking pages on today's flow.
      setPreSessionNotesEnabled(Boolean(vars.preSessionNotesEnabled));
      setMultiSessionBookingEnabled(Boolean(vars.multiSessionBookingEnabled));
    },
    [
      setCoachData,
      setCustomerId,
      setMemberPrefill,
      setRescheduleSessionId,
      setTrialInfo,
      setBookedByUserId,
      setPreSessionNotesEnabled,
      setMultiSessionBookingEnabled,
    ],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const persisted = readFromStorage();
    if (persisted) {
      return applyVars(persisted);
    }

    const vars = buildVarsFromWindow(window.__BOOKING_IFRAME_VARS__);
    if (vars) {
      writeToStorage(vars);
      applyVars(vars);
    }
  }, [applyVars, enabled]);

  // Cross-origin hosts pass iframe vars via postMessage. Once validated, this
  // bridges that transport into the existing window/global-ready-event flow.
  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }

    const handleMessage = (event: MessageEvent<unknown>) => {
      if (!isAllowedMessageOrigin(event.origin)) {
        return;
      }

      if (
        tryRemoveSessionStorageKeysFromMessage(event.data, cleanBookingState)
      ) {
        return;
      }

      if (!isBookingIframeVarsMessage(event.data)) {
        return;
      }

      const vars = buildVarsFromMessagePayload(event.data.payload);
      if (!vars) {
        return;
      }

      window.__BOOKING_IFRAME_VARS__ = vars;
      window.dispatchEvent(
        new CustomEvent(IFRAME_VARS_READY_EVENT, { detail: vars }),
      );
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [cleanBookingState, enabled]);

  // Listen for the event on normal first load and any subsequent re-injections.
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleReady = (event: Event) => {
      const detailVars =
        event instanceof CustomEvent ? parseIframeVars(event.detail) : null;
      const vars =
        detailVars ?? buildVarsFromWindow(window.__BOOKING_IFRAME_VARS__);

      if (!vars) {
        return;
      }
      writeToStorage(vars);
      applyVars(vars);
    };

    window.addEventListener(IFRAME_VARS_READY_EVENT, handleReady);
    return () => {
      window.removeEventListener(IFRAME_VARS_READY_EVENT, handleReady);
    };
  }, [applyVars, enabled]);
};
